import React, { useCallback, useContext, useMemo, useState } from 'react';

import PropTypes from 'prop-types';

import featureNames from '~/shared/constants/featureFlags';
import { PersonalizationTypes } from '~/shared/utils/embroidery';
import useHasFeature from '~/shared/utils/useHasFeature';
import { useProductDetailContext } from '~/techstyle-shared/react-components';

const ProductDetailEmbroideryContext = React.createContext();

export const useProductDetailEmbroideryContext = () =>
  useContext(ProductDetailEmbroideryContext);

const getPersonalizations = (productDetails, customizationType, cb) => {
  let bundleItemProductId = null;

  // check the top level product
  let found = productDetails?.product?.personalizations?.find(
    (item) => item.type === customizationType
  );

  // Does this product have bundleProducts
  if (!found && productDetails?.bundleProducts) {
    const bundleItemWithPersonalizations = productDetails.bundleProducts.find(
      (item) => !!(item.personalizations && item.personalizations.length)
    );

    found = bundleItemWithPersonalizations?.personalizations.find(
      (item) => item.type === customizationType
    );

    bundleItemProductId = found
      ? bundleItemWithPersonalizations.masterProductId
      : null;
  }

  // cb to shape the return object
  return cb(bundleItemProductId, found);
};

const getPersonalizationOptions = (productDetails) => {
  const { textPersonalizations, bundleItemProductId: textBundleItemProductId } =
    getPersonalizations(
      productDetails,
      PersonalizationTypes.TEXT,
      (bundleItemProductId, found) => ({
        bundleItemProductId,
        textPersonalizations: found,
      })
    );
  const { iconPersonalizations, bundleItemProductId: iconBundleItemProductId } =
    getPersonalizations(
      productDetails,
      PersonalizationTypes.LOGO,
      (bundleItemProductId, found) => ({
        bundleItemProductId,
        iconPersonalizations: found,
      })
    );

  return {
    textPersonalizations,
    iconPersonalizations,
    bundleItemProductId: textBundleItemProductId || iconBundleItemProductId,
  };
};

const getHasEmbroideryError = (productDetailContext) => {
  const { selectionStateByProductIndex } = productDetailContext;
  return Object.values(selectionStateByProductIndex).some(({ selectedSKU }) => {
    // TODO: EMBROIDERY - ADD ANY EMBROIDERY PRODUCT VALIDATION HERE
    return false;
  });
};

export default function ProductDetailEmbroideryProvider({ children }) {
  const productDetail = useProductDetailContext();

  const [productEmbroidery, setProductEmbroidery] = useState([]);

  const { textPersonalizations, iconPersonalizations, bundleItemProductId } =
    getPersonalizationOptions(productDetail);

  const { enabled: textEmbroideryFeatureEnabled } = useHasFeature(
    featureNames.TEXT_EMBROIDERY
  );
  const textEmbroideryEnabled =
    textEmbroideryFeatureEnabled && !!textPersonalizations;

  const showEmbroiderySelector = textEmbroideryEnabled;

  const addEmbroidery = useCallback((embroideryContext, apiParams = {}) => {
    setProductEmbroidery(embroideryContext);
  }, []);

  const hasEmbroideryError = useMemo(() => {
    return getHasEmbroideryError(productDetail);
  }, [productDetail]);

  const handleEmbroideryValidation = useCallback(() => {
    if (hasEmbroideryError) {
      throw new Error('Embroidery Validation Error');
    } else {
      return Promise.resolve();
    }
  }, [hasEmbroideryError]);

  const value = useMemo(
    () => ({
      showEmbroiderySelector,
      textPersonalizations,
      iconPersonalizations,
      bundleItemProductId,
      addEmbroidery,
      handleEmbroideryValidation,
      hasEmbroideryError,
      productEmbroidery,
      textEmbroideryEnabled,
    }),
    [
      showEmbroiderySelector,
      textPersonalizations,
      iconPersonalizations,
      bundleItemProductId,
      addEmbroidery,
      handleEmbroideryValidation,
      hasEmbroideryError,
      productEmbroidery,
      textEmbroideryEnabled,
    ]
  );

  return (
    <ProductDetailEmbroideryContext.Provider value={value}>
      {children}
    </ProductDetailEmbroideryContext.Provider>
  );
}

ProductDetailEmbroideryProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
